import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../config/firebaseConfig';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { 
    PlusIcon, 
    TrashIcon, 
    MagnifyingGlassIcon, 
    ChevronLeftIcon, 
    ChevronRightIcon,
    FunnelIcon 
} from '@heroicons/react/24/outline';
import { Badge, Button, Form, Spinner } from 'react-bootstrap';

const ManageCampaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // --- Filtreleme ve Pagination State'leri ---
    const [statusFilter, setStatusFilter] = useState('All');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8; // Sayfa başına kampanya sayısı

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const fetchCampaigns = async () => {
        try {
            const q = query(collection(db, 'campaigns'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setCampaigns(data);
        } catch (error) {
            console.error("Error fetching campaigns:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this campaign?")) {
            try {
                await deleteDoc(doc(db, 'campaigns', id));
                setCampaigns(prev => prev.filter(c => c.id !== id));
            } catch (error) {
                console.error("Error deleting:", error);
                alert("Failed to delete campaign.");
            }
        }
    };

    // --- Filtreleme Mantığı ---
    const filteredCampaigns = campaigns.filter(campaign => {
        if (statusFilter === 'All') return true;
        return campaign.status === statusFilter.toLowerCase(); // 'draft', 'sent', 'queued'
    });

    // --- Pagination Mantığı ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCampaigns.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);

    // Status Badge Helper
    const getStatusBadge = (status) => {
        switch(status) {
            case 'sent': return <Badge bg="success" className="px-3 py-2 rounded-pill">Sent</Badge>;
            case 'queued': return <Badge bg="info" text="dark" className="px-3 py-2 rounded-pill">Queued</Badge>;
            default: return <Badge bg="secondary" className="px-3 py-2 rounded-pill">Draft</Badge>;
        }
    };

    if (loading) return <div className="p-5 text-center"><Spinner animation="border" variant="primary"/></div>;

    return (
        <div className="container-fluid p-4 bg-light min-vh-100">
            {/* --- HEADER --- */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 fw-bold text-dark mb-1">Email Campaigns</h1>
                    <p className="text-muted mb-0">Manage, track and create new email blasts.</p>
                </div>
                {/* Create New Butonu - Artık daha zarif */}
                <Link to="/admin/create-campaign" className="btn btn-primary d-flex align-items-center gap-2 shadow-sm px-4 mt-3 mt-md-0">
                    <PlusIcon style={{width:'20px'}} />
                    <span>Create Campaign</span>
                </Link>
            </div>

            {/* --- FILTER BAR --- */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body py-3 d-flex align-items-center gap-3">
                    <FunnelIcon style={{width:'20px'}} className="text-muted"/>
                    <span className="fw-bold text-secondary">Filter by Status:</span>
                    <div className="d-flex gap-2">
                        {['All', 'Draft', 'Queued', 'Sent'].map(status => (
                            <button
                                key={status}
                                className={`btn btn-sm ${statusFilter === status ? 'btn-dark' : 'btn-outline-light text-dark border'}`}
                                onClick={() => { setStatusFilter(status); setCurrentPage(1); }}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* --- TABLE --- */}
            <div className="card shadow-sm border-0">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light text-secondary">
                            <tr>
                                <th className="ps-4 py-3">Subject</th>
                                <th>Recipients</th>
                                <th>Status</th>
                                <th>Date Created</th>
                                <th>Sent Date</th>
                                <th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map(campaign => (
                                    <tr key={campaign.id}>
                                        <td className="ps-4 fw-medium text-dark">
                                            {campaign.subject || '(No Subject)'}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="bg-light border rounded px-2 py-1 small fw-bold text-secondary">
                                                    {campaign.recipientCount || 0}
                                                </span>
                                                <small className="text-muted">users</small>
                                            </div>
                                        </td>
                                        <td>{getStatusBadge(campaign.status)}</td>
                                        <td className="text-muted small">
                                            {campaign.createdAt?.toDate().toLocaleDateString()}
                                        </td>
                                        <td className="text-muted small">
                                            {campaign.sentAt ? campaign.sentAt.toDate().toLocaleDateString() : '-'}
                                        </td>
                                        <td className="text-end pe-4">
                                            <Button 
                                                variant="outline-danger" 
                                                size="sm" 
                                                className="border-0"
                                                onClick={() => handleDelete(campaign.id)}
                                                title="Delete"
                                            >
                                                <TrashIcon style={{width:'18px'}}/>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted">
                                        No campaigns found matching your filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- PAGINATION --- */}
                {filteredCampaigns.length > itemsPerPage && (
                    <div className="card-footer bg-white border-top-0 d-flex justify-content-between align-items-center py-3">
                        <small className="text-muted">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCampaigns.length)} of {filteredCampaigns.length} entries
                        </small>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="d-flex align-items-center"
                            >
                                <ChevronLeftIcon style={{width:'16px'}} className="me-1"/> Prev
                            </Button>
                            
                            {[...Array(totalPages)].map((_, idx) => (
                                <Button 
                                    key={idx}
                                    variant={currentPage === idx + 1 ? "primary" : "outline-light text-dark border"}
                                    size="sm"
                                    onClick={() => setCurrentPage(idx + 1)}
                                >
                                    {idx + 1}
                                </Button>
                            ))}

                            <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="d-flex align-items-center"
                            >
                                Next <ChevronRightIcon style={{width:'16px'}} className="ms-1"/>
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageCampaigns;